import React, { useState, useEffect } from "react";
import { Card, List, Avatar, Badge, Typography, Spin, Popover } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { Info } from "@mui/icons-material";
import styled from "styled-components";
import { CALL_API } from "common/API";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useSelector } from "react-redux";
import { getUserInfo, getUserList } from "redux/selectors";
import { startCase } from "lodash";

const { Text, Title } = Typography;

const Wrapper = styled(Card)`
  height: 505px;
  box-shadow: rgba(0, 0, 0, 0.15) 0px 5px 15px;
  border-radius: 5px;
  border: none;
  overflow: hidden;

  .ant-card-body {
    height: 100%;
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-sizing: border-box;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    flex-shrink: 0;
  }

  .view-all {
    color: #2d53da;
    cursor: pointer;
    font-weight: 500;
    font-size: 13px;
    text-decoration: none;
  }
`;

const ListWrapper = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e5e7eb;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #d1d5db;
  }
`;

const AnnouncementItem = styled(List.Item)`
  padding: 10px 4px !important;
  border-bottom: none !important;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
    border-radius: 8px;
  }

  .ant-list-item-meta-title {
    font-size: 14px;
    font-weight: 500;
    color: #111827;
    margin-bottom: 2px !important;
  }

  .time {
    color: #6b7280;
    font-size: 13px;
  }
`;

const ItemAction = styled.div`
  display: flex;
  align-items: center;
  opacity: 0;
  transition: opacity 0.2s;
  ${AnnouncementItem}:hover & {
    opacity: 1;
  }
`;

const formatDate = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  const day = date.getDate().toString().padStart(2, "0");
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = date.getFullYear();
  const time = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${day}th ${month}, ${year} ; ${time}`;
};

const AnnouncementList = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewedIds, setViewedIds] = useState([]);

  const userInfo = useSelector(getUserInfo);
  const userList = useSelector(getUserList);

  const currentUser = userList?.find(
    (u) => String(u.userid) === String(userInfo?.userid)
  );

  const filteredAnnouncements = announcements?.filter((a) => {
    if (String(a?.createdby) === String(userInfo?.userid)) return false;

    let announcementRoleIds = a?.roleid || a?.roleId || [];
    if (typeof announcementRoleIds === "string") {
      try {
        announcementRoleIds = JSON.parse(announcementRoleIds);
      } catch (e) {
        announcementRoleIds = [];
      }
    }
    if (Array.isArray(announcementRoleIds) && announcementRoleIds?.length > 0) {
      const userRoles = (userInfo?.role_id || []).map(String);
      const hasAccess = announcementRoleIds
        .map(String)
        .some((id) => userRoles.includes(id));
      if (!hasAccess) return false;
    }

    let announcementLocationIds = a?.locationid || a?.locationId || [];
    if (typeof announcementLocationIds === "string") {
      try {
        announcementLocationIds = JSON.parse(announcementLocationIds);
      } catch (e) {
        announcementLocationIds = [];
      }
    }
    if (
      Array.isArray(announcementLocationIds) &&
      announcementLocationIds?.length > 0
    ) {
      const userBaseLocationId = userList?.find(
        (u) => String(u.userid) === String(userInfo?.userid)
      )?.baselocationid;
      const normalizedUserLocationId = Array.isArray(userBaseLocationId)
        ? userBaseLocationId.map(String)
        : [String(userBaseLocationId || "")];
      const hasLocationAccess = announcementLocationIds
        .map(String)
        .some((id) => normalizedUserLocationId.includes(id));
      if (!hasLocationAccess) return false;
    }

    return true;
  });

  const unreadCount = filteredAnnouncements?.filter((a) => {
    const alreadyViewed =
      a.vieweduserid?.includes(userInfo?.userid) || viewedIds.includes(a.id);
    return !alreadyViewed;
  })?.length;

  const history = useHistory();

  const fetchAnnouncements = async () => {
    setLoading(true);
    if (!userInfo?.userid) return;
    setError(null);
    try {
      const { code, data } = await CALL_API(
        `foresyts-user-dashboard/getannouncement`,
        "get"
      );
      if (code === "SUCCESS") {
        const filtered = data.filter((item) => {
          const announcementDate = new Date(item.createdat);
          const userCreatedDate = new Date(currentUser?.createdon);
          return announcementDate >= userCreatedDate;
        });
        setAnnouncements(filtered);
      } else {
        throw new Error("API returned non-success status");
      }
    } catch (err) {
      console.error("Failed to fetch announcements:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const markAsViewed = async (item) => {
    if (viewedIds.includes(item?.id)) return;
    setViewedIds((prev) => [...prev, item?.id]);
    try {
      await CALL_API("foresyts-user-dashboard/updateannouncement", "patch", {
        id: item?.id,
        viewStatus: true,
        viewedUserId: userInfo?.userid,
      });
    } catch (err) {
      console.error("Failed to mark announcement as viewed:", err);
      setViewedIds((prev) => prev.filter((id) => id !== item?.id));
    }
  };

  const handleItemClick = (item) => {
    markAsViewed(item);
    history.push(`/announcement-details/${item?.id}`, { viewOnly: true });
  };

  const handleViewAll = () => {
    history.push("/announcement");
  };

  const getAvatarSrc = (item) => {
    const matchedUser = userList?.find((u) => u.userid === item?.createdby);
    return matchedUser?.profileurl;
  };

  // Runs on mount and when the user changes (a second mount-only effect
  // used to fetch the same announcements twice).
  useEffect(() => {
    if (userInfo?.userid) fetchAnnouncements();
  }, [userInfo?.userid]);

  return (
    <Wrapper>
      {/* Header */}
      <div className="header">
        <div style={{ display: "flex", alignItems: "center" }}>
          <Title level={5} style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
            Announcement
          </Title>
          <Badge
            count={unreadCount}
            style={{ backgroundColor: "#ef4444", marginLeft: 6 }}
          />
        </div>
        <p className="view-all" onClick={handleViewAll}>
          View All
        </p>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "32px 0", flexShrink: 0 }}>
          <Spin />
        </div>
      ) : error ? (
        <div style={{ textAlign: "center", padding: "16px 0", flexShrink: 0 }}>
          <Text type="danger">Failed to load: {error}</Text>
        </div>
      ) : filteredAnnouncements?.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            color: "#6b7280",
            padding: "16px 0",
            flexShrink: 0,
          }}
        >
          <Text>No announcements found.</Text>
        </div>
      ) : (
        <ListWrapper>
          <List
            itemLayout="horizontal"
            dataSource={filteredAnnouncements}
            renderItem={(item, index) => {
              const isImportant = item?.level === "Important";
              const isViewed =
                item?.vieweduserid?.includes(userInfo?.userid) ||
                viewedIds.includes(item?.id);

              return (
                <AnnouncementItem
                  onClick={() => handleItemClick(item)}
                  actions={[
                    <ItemAction
                      onClick={(e) => {
                        e.stopPropagation();
                        handleItemClick(item);
                      }}
                    >
                      {isImportant ? (
                        <Popover
                          content="This is an important announcement and cannot be removed from the list."
                          overlayStyle={{ maxWidth: 200 }}
                          placement="bottom"
                        >
                          <Info
                            sx={{
                              color: "#2D53DA",
                              fontSize: "18px",
                              cursor: "pointer",
                            }}
                          />
                        </Popover>
                      ) : (
                        <CloseOutlined
                          style={{
                            color: "#57595c",
                            fontSize: "13px",
                            cursor: "pointer",
                          }}
                        />
                      )}
                    </ItemAction>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Badge dot={!isViewed} offset={[-40, 4]} color="red">
                        {getAvatarSrc(item) ? (
                          <Avatar
                            src={getAvatarSrc(item)}
                            size={46}
                            style={{ border: "2px solid #f3f4f6" }}
                          />
                        ) : (
                          <Avatar
                            style={{
                              backgroundColor: "#2D53DA",
                              border: "2px solid #f3f4f6",
                            }}
                            size={46}
                          >
                            {startCase(item?.employeename).charAt(0)}
                          </Avatar>
                        )}
                      </Badge>
                    }
                    title={
                      <span
                        style={{
                          color: isViewed ? "#111827" : "#2D53DA",
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "calc(100% - 40px)",
                        }}
                        title={item.title}
                      >
                        {item.title}
                      </span>
                    }
                    description={
                      <span className="time">{formatDate(item.createdat)}</span>
                    }
                  />
                </AnnouncementItem>
              );
            }}
          />
        </ListWrapper>
      )}
    </Wrapper>
  );
};

export default AnnouncementList;
